<template>
  <div class="relative px-4 py-8 md:pt-16 space-y-12 mx-auto max-w-screen-xl">
    <button
      v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
      class="absolute w-56 mr-4 -mt-2 right-0 block h-10 px-2 border border-transparent text-sm leading-5 font-medium text-white bg-secundario hover:bg-primario focus:outline-none focus:shadow-outline-gray focus:border-gray-700 active:bg-gray-700 transition duration-150 ease-in-out uppercase"
      @click="toggleAdminSection"
    >
      {{ isAdminSectionActive ? 'Procesos' : 'Panel de administración' }}
    </button>
    <!-- === HOME SECTION === -->
    <HomeSection v-show="!isAdminSectionActive" />

    <!-- === ADMIN HOME SECTION === -->
    <HomeAdminSection v-show="isAdminSectionActive && hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])" />

    <!-- === TAREAS === --->
    <section
      v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])"
      class="bg-white p-4 md:p-8 w-full border-t-4 border-primario shadow-md rounded-b-sm"
    >
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <div class="flex-shrink-0 mb-4">
            <p class="text-xl text-secundario font-semibold tracking-wide">
              {{ $t("lang.msg.user") }}
            </p>
            <p class="text-lg text-primario font-semibold tracking-wide -mt-1">
              {{ $t("lang.msg.pendingTasks") }} ({{ userTasks.length }})
            </p>
          </div>
          <div class="w-full p-2 border border-primario rounded-sm divide-y">
            <HomeTaskItem
              v-for="(userTask, index) in sortedUserTasks"
              :key="`userTask_${index}`"
              :title="userTask.userTaskTitle"
              :reason="userTask.userTaskExplication"
              :origen-tarea="userTask.origenTarea"
              :id-origen-tarea="userTask.idOrigenTarea"
              :check="userTask.check"
              :id-task="userTask.idUserTask"
              :task-end-date="userTask.userTaskEndDate"
              :completada="userTask.completada"
              :users="users"
              task-type="user"
              :hidden="index >= taskMaxLength"
              :show-all-tasks="showAllUserTasks"
              :url="userTask.url"
              :important="userTask.important"
              @task-sent="removeUserTask"
            />
          </div>

          <ExpandableButton
            :length="userTasks.length"
            :max-length="taskMaxLength"
            @click="showAllUserTasks = !showAllUserTasks"
          />
        </div>
        <div>
          <div class="flex-shrink-0 mb-4">
            <p class="text-xl text-secundario font-semibold tracking-wide">
              {{ $t("lang.msg.department") }}
            </p>
            <p class="text-lg text-primario font-semibold tracking-wide -mt-1">
              {{ $t("lang.msg.pendingTasks") }} ({{ departmentTasks.length }})
            </p>
          </div>
          <div class="w-full p-2 border border-primario rounded-sm divide-y">
            <HomeTaskItem
              v-for="(departmentTask, index) in sortedDepartmentTasks"
              :key="`departmentTask_${index}`"
              :title="departmentTask.departmentTaskTitle"
              :reason="departmentTask.departmentTaskExplication"
              :origen-tarea="departmentTask.origenTarea"
              :id-origen-tarea="departmentTask.idOrigenTarea"
              :check="departmentTask.check"
              :id-task="departmentTask.idDepartmentTask"
              :task-end-date="departmentTask.departmentTaskEndDate"
              :completada="departmentTask.completada"
              task-type="department"
              :hidden="index >= taskMaxLength"
              :show-all-tasks="showAllDepartmentTasks"
              :url="departmentTask.url"
              :important="departmentTask.important"
            />
          </div>

          <ExpandableButton
            :length="departmentTasks.length"
            :max-length="taskMaxLength"
            @click="showAllDepartmentTasks = !showAllDepartmentTasks"
          />
        </div>

        <div>
          <div class="flex-shrink-0 mb-4">
            <p class="text-xl text-secundario font-semibold tracking-wide">
              {{ $t("lang.msg.general") }}
            </p>
            <p class="text-lg text-primario font-semibold tracking-wide -mt-1 mb-4">
              {{ $t("lang.msg.pendingTasks") }} ({{ generalTasks.length }})
            </p>
          </div>
          <div class="w-full p-2 border border-primario rounded-sm divide-y">
            <HomeGeneralTaskItem
              v-for="(generalTask, index) in generalTasks"
              :key="`generalTask_${index}`"
              :task="generalTask"
              :hidden="index >= taskMaxLength"
              :show-all-tasks="showAllGeneralTasks"
              @click="goToGeneralTask(generalTask)"
            />
          </div>

          <ExpandableButton
            :length="generalTasks.length"
            :max-length="taskMaxLength"
            @click="showAllGeneralTasks = !showAllGeneralTasks"
          />
        </div>
      </div>
    </section>

    <!-- === CALENDAR AND TIME === -->
    <section
      v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])"
      class="w-full my-8 flex flex-col md:flex-row md:items-start justify-between mb-4 space-y-6 md:space-y-0"
    >
      <!-- CALENDAR  -->
      <div class="w-full max-w-72 sm:w-64 flex flex-col items-center sm:items-start mx-auto md:m-0 space-y-4">
        <v-calendar
          @dayclick="goToCalendar"
          :attributes="attrs"
          mode="multiple"
          title-position="left"
          is-inline
          @update:from-page="fetchAllGeneralTasks($event)"
        />
        <ButtonRedirect route="calendar">
          {{ $t("lang.msg.openCalendar") }}
        </ButtonRedirect>
      </div>
      <!-- TIME -->
      <div class="w-full max-w-72 mx-auto mt-8 md:m-0 space-y-4">
        <div class="bg-white py-4 flex flex-col items-end rounded-sm">
          <div class="w-full text-center">
            <p class="text-5xl text-primario mb-4 border-b-2 border-gray-300">
              {{ currentTime }}
            </p>
            <p class="text-sm whitespace-no-wrap">
              {{ today }}
            </p>
          </div>
        </div>
        <ButtonAction @action="printTasks" large>
          {{ $t("lang.msg.printTasks") }}
        </ButtonAction>
      </div>
    </section>
  </div>
</template>

<script>
import jsPDF from "jspdf";
import "jspdf-autotable";

import { mapState } from "vuex";
import HomeSection from "@/components/HomeSection";
import HomeAdminSection from "@/components/HomeAdminSection";

// MIXINS
import UtilsMixin from "@/components/mixins/UtilsMixin";
import TasksMixin from "@/components/mixins/TasksMixin";

export default {
  name: "Home",
  mixins: [UtilsMixin, TasksMixin],
  components: {
    HomeSection,
    HomeAdminSection
  },
  computed: {
    ...mapState("language", ["language"]),
    ...mapState("auth", ["user"]),
    today() {
      return this.$moment().locale(this.language).format("dddd, LL");
    },
    sortedUserTasks() {
        return this.userTasks.slice(0).sort((a, b) => b.important - a.important)
    },
    sortedDepartmentTasks() {
        return this.departmentTasks.slice(0).sort((a, b) => b.important - a.important)
    }
  },
  data() {
    return {
      toHome: false,
      users: [],
      userTasks: [],
      departmentTasks: [],
      generalTasks: [],

      timeout: null,
      currentTime: this.$moment().format("HH:mm:ss"),
      isAdminSectionActive: false,
      taskMaxLength: 10,
      showAllUserTasks: false,
      showAllDepartmentTasks: false,
      showAllGeneralTasks: false
    };
  },
  created() {
    this.toHome = this.$route.params.toHome;
    this.isAdminSectionActive = this.isAdminActive();
    this.timeout = setInterval(() => this.updateCurrentTime(), 1 * 1000);
    this.fetchUserTasks();
    this.fetchDepartmentTasks();
    this.fetchGeneralTasks();
    this.fetchCalendarTasks();
    this.fetchUsers();
  },
  methods: {
    goToGeneralTask(task){
        this.$router.push({ name: "calendar",params:{today:task.start} });
    },
    fetchUsers() {
      this.$axios.get("api/Users/GetUsers").then((response) => {
        this.users = this.getActive(response.data, 'active');
      });
    },
    updateCurrentTime() {
      this.currentTime = this.$moment().format("HH:mm:ss");
    },
    toggleAdminSection() {
      this.isAdminSectionActive = !this.isAdminSectionActive;
      localStorage.setItem("isAdminSectionActive", this.isAdminSectionActive);
    },
    isAdminActive() {
      return (
        localStorage.getItem("isAdminSectionActive") === "true" &&
        this.hasRoles(["DIRECCIÓN", "ADMIN", "EQUIPO EUROPROSEM"]) &&
        this.toHome === undefined
      );
    },
    fetchUserTasks() {
      this.$axios
        .post(`api/UserTasks/GetAllUserTask`, {
          idUser: this.user.id
        })
        .then((response) => {
          this.userTasks = this.sortTasksByActive(response.data);
          this.userTasks.map((u)=>u.important=false)
        });
    },
    fetchDepartmentTasks() {
      this.$axios
        .post(`api/DepartmentTasks/GetAllDepartmentTask`, {
          idUser: this.user.id
        })
        .then((response) => {
          this.departmentTasks = this.sortTasksByActive(response.data);
        });
    },
    fetchAllGeneralTasks(data) {
      this.fetchCalendarTasks(data);
      this.fetchGeneralTasks(data);
    },
    fetchGeneralTasks(data) {
      let {month, year} = 0;

      if (data === undefined) {
        month = parseInt(this.$moment().format("M"));
        year = parseInt(this.$moment().format("YYYY"));
      } else {
        month = data.month;
        year = data.year;
      }

      this.$axios
        .post(`api/GeneralTasks/GetAllGeneralTask`, {
          idUser: this.user.id,
          mes: month,
          año: year
        }).then(response => {
          this.generalTasks = response.data;
        });
    },
    sortTasksByActive(data) {
      let activeTasks = [];
      let completedTasks = [];

      data.forEach(element => {
        if (element.active) activeTasks.push(element);
        else completedTasks.push(element);
      });

      return activeTasks.concat(completedTasks);
    },
    removeUserTask(task_id) {
      this.userTasks = this.userTasks.filter( task => {
        return task.idUserTask != task_id
      });
    },
    printTasks() {
      // Only pt supported (not mm or in)
      var doc = new jsPDF("p", "pt");

      // Format User and Department data fields.
      let userData = this.userTasks.map((el) => {
        return {
          active: el.active ? "Si" : "No",
          userTaskTitle: el.userTaskTitle.replace(/<[^>]*>/g, ""),
          userTaskExplication: el.userTaskExplication.replace(/<[^>]*>/g, "")
        };
      });

      var departmentData = this.departmentTasks.map((el) => {
        return {
          active: el.active ? "Si" : "No",
          departmentTaskTitle: el.departmentTaskTitle.replace(/<[^>]*>/g, ""),
          departmentTaskExplication: el.departmentTaskExplication.replace(
            /<[^>]*>/g,
            ""
          )
        };
      });

      // User Tasks table.
      var userColumns = [
        { title: "Título", dataKey: "userTaskTitle" },
        { title: "Motivo", dataKey: "userTaskExplication" },
        { title: "Activo", dataKey: "active" }
      ];

      doc.text("Tareas de los usuarios", 40, 30);
      doc.autoTable(userColumns, userData, {
        theme: "striped",
        margin: { top: 40 }
      });

      // Adds a new page
      doc.addPage();

      // Department Tasks table.
      var departmentColumns = [
        { title: "Título", dataKey: "departmentTaskTitle" },
        { title: "Motivo", dataKey: "departmentTaskExplication" },
        { title: "Activo", dataKey: "active" }
      ];

      doc.text("Tareas del departamento", 40, 30);
      doc.autoTable(departmentColumns, departmentData, {
        theme: "striped",
        margin: { top: 40 }
      });

      let currentDate = this.$moment().format("DDMMYYYY HHmmss");

      // Print task in PDF.
      doc.save(`Tareas ${currentDate}.pdf`);
    }
  },
  beforeUnmount() {
    clearTimeout(this.timeout);
  }
};
</script>

<style>
.carta {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
}
.carta span {
  opacity: 0;
  transition: opacity 0.5s, visibility 0.5s;
}
.carta svg {
  opacity: 1;
  transition: opacity 0.5s, visibility 0.5s;
}

.carta:hover span {
  opacity: 1;
}
.carta:hover svg {
  opacity: 0.2;
}
</style>
